import {memo, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation} from 'react-router-dom';
import {BASE_URL, metaMap, schemaMap} from '../data/schema';
import {useAppSelector, useTranslation} from '../hooks';
import {languageState} from '../redux/slices/app';

const CanonicalTag = () => {
  const {t} = useTranslation();
  const selectedLanguage = useAppSelector(languageState);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.setAttribute('lang', selectedLanguage);
  }, [selectedLanguage]);

  const ogLocale = useMemo(
    () => (selectedLanguage === 'en' ? 'en_US' : 'tr_TR'),
    [selectedLanguage],
  );

  const trPath = location.pathname.replace(/^\/en/, '/tr');
  const enPath = location.pathname.replace(/^\/tr/, '/en');
  const currentSchema = schemaMap[location.pathname];

  const currentMeta = metaMap[location.pathname] || {
    title: t('metaTitle'),
    description: t('metaDescription2'),
  };

  return (
    <>
      <Helmet
      // script={[
      //   {
      //     type: 'application/ld+json',
      //     innerHTML: JSON.stringify(currentSchema),
      //   },
      // ]}
      >
        <title>{currentMeta.title}</title>
        <meta name="title" content={currentMeta.title} />
        <meta name="description" content={currentMeta.description} />
        <link
          rel="canonical"
          href={`https://www.e-metal.com.tr${location.pathname}`}
        />
        <link rel="alternate" hrefLang="tr" href={`${BASE_URL}${trPath}`} />
        <link rel="alternate" hrefLang="en" href={`${BASE_URL}${enPath}`} />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${BASE_URL}${trPath}`}
        />
        {/* <!-- Open Graph / Facebook --> */}
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="e-Metal" />
        <meta property="og:url" content="https://www.e-metal.com.tr" />
        <meta property="og:title" content={t('metaTitle')} />
        <meta property="og:description" content={t('metaDescription')} />
        <meta
          property="og:image"
          content="https://www.e-metal.com.tr/bakir.png"
        />

        {/* <!-- Twitter --> */}
        {/* <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://twitter.com/e-metal" />
        <meta property="twitter:title" content={t('metaTitle')} />
        <meta property="twitter:description" content={t('metaDescription')} />
        <meta
          property="twitter:image"
          content="https://www.e-metal.com.tr/bakir.png"
        /> */}
      </Helmet>
      {currentSchema && <JsonLd data={currentSchema} />}
    </>
  );
};

export default memo(CanonicalTag);

type JsonLdProps = {
  data: Record<string, any>;
};

export const JsonLd = ({data}: JsonLdProps) => {
  useEffect(() => {
    if (!data) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [data]);

  return null;
};
